import { useTheme } from "@emotion/react";
import { Chart as ChartJS } from "chart.js";
import { BarTimeChartSynced } from "./bar_time_chart";
import { getBarTimeChartOptions, applyAutoScale } from "./chart_options";

export function GridChart({ metering, multiDay = false }) {
  const theme = useTheme();
  const utctime = metering.map((el) => new Date(1000 * el.utctime));
  const grid_offtake = metering.map((el) => el.grid_offtake);
  const grid_inject = metering.map((el) => -el.grid_inject);
  const grid_setpoint = metering.map((el) => el.grid_setpoint);

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parseBoolean = (value) => {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true") return true;
      if (normalized === "false") return false;
    }
    return null;
  };

  const maxOfftakeSetpointSeries = metering.map((entry) => {
    const meteringValue = parseNumber(entry?.max_grid_offtake_sp);
    return meteringValue !== null ? Math.abs(meteringValue) : null;
  });
  const maxOfftakeSetpointSeriesActive = metering.map((entry, index) => {
    const peakShavingOfftake = parseBoolean(entry?.peak_shaving_offtake);
    return peakShavingOfftake === true ? maxOfftakeSetpointSeries[index] : null;
  });
  const maxInjectSetpointSeries = metering.map((entry) => {
    const meteringValue = parseNumber(entry?.max_grid_inject_sp);
    return meteringValue !== null ? -Math.abs(meteringValue) : null;
  });
  const maxInjectSetpointSeriesActive = metering.map((entry, index) => {
    const peakShavingInject = parseBoolean(entry?.peak_shaving_inject);
    return peakShavingInject === true ? maxInjectSetpointSeries[index] : null;
  });
  const hasActiveMaxOfftakeSetpointSeries = maxOfftakeSetpointSeriesActive.some((value) => value !== null);
  const hasActiveMaxInjectSetpointSeries = maxInjectSetpointSeriesActive.some((value) => value !== null);

  const data = {
    labels: utctime,
    datasets: [
      {
        fill: true,
        label: "Grid Offtake",
        data: grid_offtake,
        borderWidth: 0,
        borderColor: theme.palette.grid.main,
        backgroundColor: theme.palette.grid.main,
        cubicInterpolationMode: "monotone",
        pointStyle: false,
      },
      {
        fill: true,
        label: "Grid Inject",
        data: grid_inject,
        borderWidth: 0,
        borderColor: theme.palette.grid.secondary,
        backgroundColor: theme.palette.grid.secondary,
        cubicInterpolationMode: "monotone",
        pointStyle: false,
      },
      {
        fill: false,
        label: "Grid Setpoint",
        data: grid_setpoint,
        borderWidth: 2,
        borderColor: "grey",
        backgroundColor: "transparent",
        cubicInterpolationMode: "monotone",
        pointStyle: false,
        type: "line",
        order: -1,
      },
      ...(hasActiveMaxOfftakeSetpointSeries
        ? [
            {
              fill: false,
              label: "Max Offtake Peak",
              data: maxOfftakeSetpointSeriesActive,
              borderWidth: 2,
              borderColor: theme.palette.grid_reference_max_offtake.main,
              backgroundColor: "transparent",
              stepped: "middle",
              spanGaps: false,
              pointStyle: false,
              pointRadius: 0,
              pointHoverRadius: 0,
              pointHitRadius: 0,
              type: "line",
              order: -5,
            },
          ]
        : []),
      ...(hasActiveMaxInjectSetpointSeries
        ? [
            {
              fill: false,
              label: "Max Inject Peak",
              data: maxInjectSetpointSeriesActive,
              borderWidth: 2,
              borderColor: theme.palette.grid_reference_max_offtake.main,
              backgroundColor: "transparent",
              stepped: "after",
              spanGaps: false,
              pointStyle: false,
              pointRadius: 0,
              pointHoverRadius: 0,
              pointHitRadius: 0,
              type: "line",
              order: -5,
            },
          ]
        : []),
    ],
  };

  const options = getBarTimeChartOptions(multiDay);
  applyAutoScale(
    options,
    [
      grid_offtake,
      grid_inject,
      grid_setpoint,
      maxOfftakeSetpointSeriesActive,
      maxInjectSetpointSeriesActive,
    ],
    "W",
  );
  options.scales.y.stacked = false;
  options.plugins = options.plugins || {};
  options.plugins.legend = options.plugins.legend || {};
  options.plugins.legend.labels = options.plugins.legend.labels || {};
  options.plugins.legend.labels.filter = (legendItem, chartData) => {
    const dataset = chartData?.datasets?.[legendItem.datasetIndex];
    return !dataset?.skipLegend;
  };
  options.plugins.legend.labels.generateLabels = (chart) => {
    const baseItems = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

    return baseItems.map((item) => {
      const dataset = chart?.data?.datasets?.[item.datasetIndex];
      const isMaxPeakDataset = dataset?.label === "Max Offtake Peak" || dataset?.label === "Max Inject Peak";

      if (!isMaxPeakDataset) {
        return item;
      }

      return {
        ...item,
        fillStyle: "rgba(0, 0, 0, 0)",
        strokeStyle: dataset?.borderColor ?? item.strokeStyle,
        lineWidth: dataset?.borderWidth ?? 2,
        lineDash: undefined,
      };
    });
  };
  options.plugins.tooltip = options.plugins.tooltip || {};
  options.plugins.tooltip.callbacks = options.plugins.tooltip.callbacks || {};
  options.plugins.tooltip.callbacks.label = (tooltipItem) => {
    const datasetLabel = tooltipItem?.dataset?.label;
    return `${datasetLabel}: ${tooltipItem?.formattedValue}`;
  };

  return <BarTimeChartSynced data={data} options={options} />;
}
